import type React from "react";
import { useMemo, useRef } from "react";

import { Map } from "@similarweb/pro-logic-components";
import { MiniFlexTable } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { useMapGeoJson } from "app/common/hooks";
import { useInViewport } from "app/custom-hooks/useInViewport";
import { translate } from "app/filters/i18nFilter";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { useGeoBreakdownQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useGeoBreakdownQuery";
import Card from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/Card";
import CardHeader from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardHeader/CardHeader";
import { LAZY_CARD_VIEWPORT_OPTIONS } from "app/pages/sales-intelligence/pages/company-analysis/constants";
import { getColumnsConfig } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/GeoBreakdown/columns";
import { ACTIVE_CARD_GEO } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/GeoBreakdown/constants";
import { EmptyState } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/GeoBreakdown/EmptyState";
import { GeoBreakdownLoader } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/GeoBreakdown/GeoBreakdownLoader";
import { getHighchartsConfig } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/GeoBreakdown/highchartsConfig";
import { StyledTableWrapper } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/GeoBreakdown/styles";
import CountryService from "app/services/CountryService";
import { useTranslation } from "pro-features/components/WithTranslation";

export const GeoBreakdown: React.FC = () => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const domain = params.key;

    const cardRef = useRef<HTMLDivElement>(null);
    const isVisible = useInViewport(cardRef, {
        ...LAZY_CARD_VIEWPORT_OPTIONS,
        forceVisible: params.activeCard === ACTIVE_CARD_GEO,
    });

    const t = useTranslation();

    const { data, isFetched, isLoading } = useGeoBreakdownQuery(domain, { enabled: isVisible });
    const { data: mapGeoJson, isLoading: isMapGeoJsonLoading } = useMapGeoJson({ enabled: isVisible });
    const isEmpty = (!data || data.length === 0) && isFetched;
    const isCardLoading = !isVisible || isLoading;
    const tableData = useMemo(() => (data ?? []).sort((a, b) => b.share - a.share).slice(0, 5), [data]);

    const mapData = useMemo(() => {
        return (data ?? [])
            .map((item) => {
                const country = CountryService.getCountryById(item.country);
                if (!country) {
                    return undefined;
                }
                return {
                    "hc-key": country.code,
                    value: item.share,
                };
            })
            .filter(Boolean);
    }, [data]);

    const chartConfig = useMemo(() => getHighchartsConfig(), []);
    const columnsConfig = useMemo(() => getColumnsConfig(), []);

    return (
        <Card
            ref={cardRef}
            dataAutomation="geo-breakdown-card"
            isLoading={isCardLoading}
            loader={<GeoBreakdownLoader />}
            isEmpty={isEmpty}
            emptyState={<EmptyState />}
            header={
                <CardHeader
                    title={translate("si.company-analysis.geo-breakdown.card.title")}
                    infoTooltip={translate("si.company-analysis.geo-breakdown.card.tooltip")}
                />
            }
            renderContent={() => (
                <Flex
                    padding="32px 46px 32px 32px"
                    justifyContent="space-between"
                >
                    <Box flex="0 1 48%">
                        <Map
                            config={chartConfig}
                            mapData={mapGeoJson}
                            data={mapData}
                            isLoading={isMapGeoJsonLoading}
                            style={{
                                height: "100%",
                            }}
                        />
                    </Box>
                    <Box flex="0 1 48%">
                        <StyledTableWrapper withBottomBorder={tableData.length < 5}>
                            <MiniFlexTable
                                data={tableData}
                                columns={columnsConfig}
                                useStyledTable
                                translations={{
                                    outOf: t("common.minitable.pager.out_of"),
                                    pages: t("Common.minitable.pager.pages"),
                                }}
                            />
                        </StyledTableWrapper>
                    </Box>
                </Flex>
            )}
        />
    );
};
